import type { Virtualizer } from '@tanstack/react-virtual';
import { flushSync } from 'react-dom';

import { devLog } from '../../../../core/utils/logging.js';
import { DATA_TABLE_DEFAULT_SCROLL_OPTIONS } from '../../constants.js';
import type { Table, TableFeature } from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';
import type { DataTableRowData } from '../../public.api.js';
import { memo } from '../../tanstack-table-core-override/src/index.js';

/**
 * Feature implementation for virtual scrolling.
 * @internal
 */
export const DataTableVirtualScrolling: TableFeature = {
  createTable<TData extends DataTableRowData>(table: Table<TData>): void {
    let _rowVirtualizer: Virtualizer<HTMLDivElement, Element> | undefined =
      undefined;
    let _columnVirtualizer: Virtualizer<HTMLDivElement, Element> | undefined =
      undefined;
    // Save all column widths that have already been measured outside the virtualizer's measurementsCache.
    // This ensures measured widths are available immediately and don't get lost when column visibility changes.
    const measuredColumnWidths = new Map<string, number>();

    table.registerVirtualizers = (
      rowVirtualizer: Virtualizer<HTMLDivElement, Element>,
      columnVirtualizer: Virtualizer<HTMLDivElement, Element>,
    ) => {
      _rowVirtualizer = rowVirtualizer;
      _columnVirtualizer = columnVirtualizer;
    };

    table.getRowVirtualizer = () => {
      return _rowVirtualizer;
    };

    table.getColumnVirtualizer = () => {
      return _columnVirtualizer;
    };

    table.getRowVirtualizationContainer = () => {
      return _rowVirtualizer?.scrollElement ?? undefined;
    };

    table.getRowHeight = (rowId) => {
      if (
        !_rowVirtualizer ||
        !rowId ||
        _rowVirtualizer.measurementsCache.length === 0
      ) {
        return 0;
      }

      const virtualRowIndex = _rowVirtualizer
        .getVirtualItems()
        .find((item) => item.key === rowId)?.index;

      if (virtualRowIndex === undefined) {
        return 0;
      }

      return (
        _rowVirtualizer.measurementsCache.find(
          (r) => r.index === virtualRowIndex,
        )?.size ?? 0
      );
    };

    table.getRowScrollOffset = () => _rowVirtualizer?.scrollOffset;

    table.scrollToRowOffset = (offset: number | undefined | null) => {
      if (offset !== undefined && offset !== null) {
        _rowVirtualizer?.scrollToOffset(
          offset,
          DATA_TABLE_DEFAULT_SCROLL_OPTIONS,
        );
      }
    };

    // Scroll to a given `rowId` using `rowVirtualizer.scrollToIndex`
    table.scrollToRow = (
      rowId: string,
      align:
        | 'start'
        | 'center'
        | 'end' = DATA_TABLE_DEFAULT_SCROLL_OPTIONS.align,
    ) => {
      const rowVirtualizer = _rowVirtualizer;

      if (!rowVirtualizer) {
        devLog('[Table - VirtualScrolling] No rowVirtualizer was registered');
        return;
      }

      // Helper function to scroll to the row when table is scrollable and rowVirtualizer is available
      const scrollToTargetRow = () => {
        requestAnimationFrame(() => {
          // This code works because `allRows` will contain (only!) expanded subRows on the top level.
          // When scrolling to a subRow, the row has to be expanded beforehand.
          const allRows = table.getRowModel().rows;
          const rowIndex = allRows.findIndex((row) => row.id === rowId);

          if (rowIndex === -1) {
            devLog(
              `[Table - VirtualScrolling] Row with id: "${rowId}" not found`,
            );
            return;
          }

          rowVirtualizer.scrollToIndex(rowIndex, {
            behavior: DATA_TABLE_DEFAULT_SCROLL_OPTIONS.behavior,
            align: align,
          });
        });
      };

      if (!table.options.enablePagination) {
        // If pagination is not enabled, we can scroll directly.
        scrollToTargetRow();
        return;
      }

      const prePaginationRows = table.getPrePaginationRowModel().rows;
      const prePaginationRowIndex = prePaginationRows.findIndex(
        (row) => row.id === rowId,
      );

      if (prePaginationRowIndex === -1) {
        devLog(`[Table - VirtualScrolling] Row with id: "${rowId}" not found`);
        return;
      }

      const paginationState = table.getState().pagination;
      const pageSize = paginationState.pageSize;

      if (!Number.isFinite(pageSize) || pageSize <= 0) {
        devLog(
          `[Table - VirtualScrolling] Invalid page size "${String(
            pageSize,
          )}" while scrolling to row with id: "${rowId}"`,
        );
        return;
      }

      const targetPageIndex = Math.floor(prePaginationRowIndex / pageSize);

      if (targetPageIndex !== paginationState.pageIndex) {
        // flushSync forces React to commit the page change synchronously,
        // so scrollToTargetRow can immediately find the new page's rows.
        flushSync(() => {
          table.setPageIndex(targetPageIndex, 'internal');
        });
      }

      scrollToTargetRow();
    };

    function measureRows(): void {
      // Early exit if the rowVirtualizer or scrollElement are not present
      if (!_rowVirtualizer?.scrollElement) {
        return;
      }

      // Select the grid defining container.
      const gridContainer = _rowVirtualizer.scrollElement.querySelector(
        '[style*="grid-template-rows"]',
      );

      // Early exit if gridContainer is not a HTMLElement
      if (gridContainer === null || !(gridContainer instanceof HTMLElement)) {
        return;
      }

      // Store the original grid row template so it can be applied afterwards.
      const originalGridTemplate = gridContainer.style.gridTemplateRows;
      try {
        let templateRows = table.getHeaderGridTemplateRows();
        const rowCount = _rowVirtualizer.getVirtualItems().length;

        if (rowCount > 0) {
          // Set the row template for body rows to min-content so all rows can be measured by their actual size.
          const bodyRows = `repeat(${rowCount}, min-content)`;
          // Spacer row height can be defaulted to 0 as it's not relevant for the measuring.
          templateRows = `${templateRows} 0px ${bodyRows} [body-end]`;
        }

        gridContainer.style.gridTemplateRows = templateRows;

        // Select all row elements with the data attribute 'data-row'
        const measureRows =
          _rowVirtualizer.scrollElement.querySelectorAll('[data-row]');

        // Iterate over each row relevant for measuring
        for (const measureRow of Array.from(measureRows)) {
          // Ensure the element is an instance of HTMLElement
          if (!(measureRow instanceof HTMLElement)) {
            continue;
          }
          _rowVirtualizer.measureElement(measureRow);
        }

        const virtualItems = _rowVirtualizer.getVirtualItems();
        const virtualItemsLength = virtualItems.length;

        const firstMeasuredRow =
          measureRows.item(0)?.getAttribute('data-row') ?? null;
        const isDifferentRowsVisible =
          firstMeasuredRow !== null &&
          virtualItemsLength > 0 &&
          Number(firstMeasuredRow) !== virtualItems[0].index;

        // If different rows are measured than those that are currently visible or
        // if fewer items are measured than would now fit into the scrollContainer
        // we reset the measurements cache and allow it to remeasure all items again.
        if (isDifferentRowsVisible || measureRows.length < virtualItemsLength) {
          _rowVirtualizer.measure();
        }
      } finally {
        // Restore the original grid template.
        gridContainer.style.gridTemplateRows = originalGridTemplate;
      }
    }

    table.remeasureRows = memo(
      (containerWidth) => {
        const state = table.getState();
        const expandingState = table.options.enableExpanding
          ? Object.keys(state.expanded).length !== 0 && state.expanded
          : undefined;
        const rowDetailsState = table.options.enableRowDetails
          ? state.rowDetails
          : undefined;
        const sortingState = table.options.enableSorting
          ? state.sorting
          : undefined;
        return [
          containerWidth,
          expandingState,
          rowDetailsState,
          sortingState,
          state.columnLineWrap,
          state.visualVariantConfig.rowDensity,
          state.visualVariantConfig.rowSeparation,
          state.columnSizing,
          state.pagination.pageIndex,
          state.pagination.pageSize,
          state.columnVisibility,
          state.rowOrder,
          table.options.data,
          table.options.columns,
        ];
      },
      (containerWidth) => {
        if (containerWidth === 0) {
          return;
        }

        // Need to shift this into the next update cycle in order to make sure that
        // all styles that could change are actually already updated on the table.
        // Without the shift, lineWrap values might not be updated yet when measuring.
        table._queue(measureRows);
      },
      getMemoOptions(table.options, 'debugTable', 'table.remeasureRow'),
    );

    function measureColumns(columnIdOrIds: string | string[]): void {
      // Early exit if the columnVirtualizer or scrollElement are not present
      if (!_columnVirtualizer?.scrollElement) {
        return;
      }

      // Select the grid defining container.
      const gridContainer = _columnVirtualizer.scrollElement.querySelector(
        '[style*="grid-template-columns"]',
      );

      // Early exit if gridContainer is not a HTMLElement
      if (gridContainer === null || !(gridContainer instanceof HTMLElement)) {
        return;
      }

      // Store the original grid row template so it can be applied afterwards.
      const originalGridTemplate = gridContainer.style.gridTemplateColumns;
      try {
        const columnIds = Array.isArray(columnIdOrIds)
          ? columnIdOrIds
          : [columnIdOrIds];
        const columnIdsSet = new Set(columnIds);

        // Reset measurements for the specified columns to trigger a remeasure when regenerating the grid template
        table.resetMeasuredColumnWidths(columnIds);
        gridContainer.style.gridTemplateColumns =
          table.getGridTemplateColumns();

        // Select all cells in the first body row
        const firstRowCells = _columnVirtualizer.scrollElement.querySelectorAll(
          '[role="rowgroup"]:nth-child(2) [data-row]:first-child [data-columnid]',
        );

        // Iterate over each cell relevant for measuring
        for (const cell of Array.from(firstRowCells)) {
          const columnId = cell instanceof HTMLElement && cell.dataset.columnid;
          // Only measure if the element is an instance of HTMLElement
          // and its columnId is in the list of columnIds to measure
          if (columnId && columnIdsSet.has(columnId)) {
            _columnVirtualizer.measureElement(cell);
          }
        }

        // assign the updated grid template with the newly measured column widths
        gridContainer.style.gridTemplateColumns =
          table.getGridTemplateColumns();
      } catch {
        // Restore the original grid template if something fails.
        gridContainer.style.gridTemplateColumns = originalGridTemplate;
      }
    }

    table.remeasureColumn = (columnId?: string) => {
      if (columnId === undefined || columnId.length === 0) {
        return;
      }

      table._queue(() => measureColumns(columnId));
    };

    table.remeasureAllColumns = () => {
      const columnIdsToRemeasure =
        table.getVisibleVirtualColumnIdsWithoutBuiltin();

      if (columnIdsToRemeasure.length === 0) {
        return;
      }

      table._queue(() => measureColumns(columnIdsToRemeasure));
    };

    table.hasColumnCellsRendered = (columnId: string) => {
      if (!_columnVirtualizer?.scrollElement) {
        return false;
      }

      return (
        _columnVirtualizer?.scrollElement.querySelector(
          `[role="cell"][data-columnid="${CSS.escape(columnId)}"]`,
        ) !== null
      );
    };

    table.getMeasuredColumnWidths = () => {
      return measuredColumnWidths;
    };

    table.getMeasuredColumnWidth = (id: string) => {
      return measuredColumnWidths.get(id);
    };

    table.resetMeasuredColumnWidths = (ids?: string[]) => {
      if (ids) {
        for (const id of ids) {
          measuredColumnWidths.delete(id);
        }
      } else {
        measuredColumnWidths.clear();
      }
    };

    table.setMeasuredColumnWidth = (id: string, width: number) => {
      measuredColumnWidths.set(id, width);
    };

    table.getHasVirtualizedRows = memo(
      () => [_rowVirtualizer?.getVirtualItems().length],
      (virtualItemsLength) => {
        if (virtualItemsLength === undefined || virtualItemsLength === 0) {
          return false;
        }

        const rows = table.getRowModel().rows;

        return rows.length > virtualItemsLength;
      },
      getMemoOptions(
        table.options,
        'debugTable',
        'table.getHasRowVirtualization',
      ),
    );
  },
};
